import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Printer, ImagePlus, X, Upload, Sparkles, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StickerRenderer, labelDimensions } from "@/components/StickerRenderer";

const EMPTY = {
  label_type: "copper",
  product_name: "",
  material_content: "",
  net_quantity: "",
  actual_capacity: "",
  country_of_origin: "India",
  pkd_month_year: "",
  batch_no: "",
  mrp: "",
  mop: "",
  included_of_taxes: true,
  ean_code: "",
  tagline: "",
  mfd_pkd_by: "",
  connect_with_us: "",
  brand_name: "",
  show_isi: true,
  isi_number: "",
  isi_cml: "",
  product_image: "",
  color: "",
  model_no: "",
  packed_on: "",
  marketed_by: "",
  query_email: "",
  customer_care: "",
};

export default function EditorPage({ mode }) {
  const { id } = useParams();
  const isEdit = mode === "edit";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [refImage, setRefImage] = useState(null); // reference sticker (not saved)
  const [refFile, setRefFile] = useState(null); // raw file to send to vision extractor
  const [extracting, setExtracting] = useState(false);
  const [defaults, setDefaults] = useState(null); // { copper: {...}, india: {...}, china: {...} }
  const productImgRef = useRef(null);
  const refImgRef = useRef(null);
  // Snapshot of values this component auto-injected from Settings — used to
  // decide whether a field is "still auto-filled" (and therefore safe to
  // overwrite when the user switches label_type) vs. user-typed.
  const autoFilledRef = useRef({});

  // Load per-user default meta fields once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setDefaults(data || {});
      } catch {
        setDefaults({});
      }
    })();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      (async () => {
        try {
          const { data } = await api.get(`/products/${id}`);
          setForm({ ...EMPTY, ...data });
        } catch {
          toast.error("Product not found");
          navigate("/");
        }
      })();
    }
  }, [id, isEdit, navigate]);

  // On NEW labels, apply defaults for the current label_type. A field is
  // written if it's empty OR still holds a value we auto-filled ourselves
  // (i.e. user hasn't overridden it). This lets label_type switches replace
  // stale defaults without stomping typed values.
  useEffect(() => {
    if (isEdit) return;
    if (!defaults) return;
    const d = defaults[form.label_type] || {};
    const merged = { ...form };
    const nextAuto = { ...autoFilledRef.current };
    let changed = false;
    for (const k of ["country_of_origin", "mfd_pkd_by", "connect_with_us",
                     "marketed_by", "query_email", "customer_care"]) {
      const cur = merged[k];
      const isEmpty = typeof cur !== "string" || cur.trim() === "";
      const overrideCountry = k === "country_of_origin" && cur === "India";
      const stillAutoFilled = autoFilledRef.current[k] !== undefined && cur === autoFilledRef.current[k];
      const shouldWrite = d[k] && (isEmpty || overrideCountry || stillAutoFilled);
      if (shouldWrite && merged[k] !== d[k]) {
        merged[k] = d[k];
        nextAuto[k] = d[k];
        changed = true;
      } else if (shouldWrite) {
        nextAuto[k] = d[k];
      } else if (!d[k] && stillAutoFilled) {
        // Previous type had a default here, new type doesn't — clear the
        // stale auto-filled value so the user isn't stuck with wrong data.
        merged[k] = "";
        delete nextAuto[k];
        changed = true;
      }
    }
    autoFilledRef.current = nextAuto;
    if (changed) setForm(merged);
  }, [isEdit, defaults, form.label_type]);

  // If the user types into any auto-populated field, forget that we auto-
  // filled it so future label_type switches won't overwrite the typed value.
  const forgetAutoFill = (key) => {
    if (autoFilledRef.current[key] !== undefined) {
      const next = { ...autoFilledRef.current };
      delete next[key];
      autoFilledRef.current = next;
    }
  };

  const set = (k) => (e) => {
    const val = e && e.target ? e.target.value : e;
    forgetAutoFill(k);
    setForm((f) => ({ ...f, [k]: val }));
  };

  const readImage = (file, cb) => {
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };

  const onPickProductImage = (e) => {
    const file = e.target.files?.[0];
    if (file) readImage(file, (dataUrl) => setForm((f) => ({ ...f, product_image: dataUrl })));
  };

  const onPickRefImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRefFile(file);
    readImage(file, setRefImage);
    // Kick off auto-extraction immediately.
    autoExtract(file);
  };

  const autoExtract = async (file) => {
    if (!file) return;
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/extract-from-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      // Merge non-empty extracted fields into the form, preserving anything
      // the user has already typed.
      setForm((f) => {
        const merged = { ...f };
        for (const [k, v] of Object.entries(data || {})) {
          const cur = merged[k];
          const isEmptyStr = typeof cur === "string" && cur.trim() === "";
          if (v !== undefined && v !== null && v !== "" && (isEmptyStr || cur === undefined)) {
            merged[k] = v;
          }
        }
        return merged;
      });
      const filled = Object.entries(data || {}).filter(
        ([, v]) => typeof v === "string" && v.trim() !== ""
      ).length;
      toast.success(`Auto-filled ${filled} field${filled !== 1 ? "s" : ""} from the sticker image`);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Auto-extract failed";
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  const save = async () => {
    if (!form.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/products/${id}`, form);
        toast.success("Product saved");
      } else {
        const { data } = await api.post("/products", form);
        toast.success("Product created");
        navigate(`/edit/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveAndPrint = async () => {
    if (!form.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }
    setSaving(true);
    try {
      let productId = id;
      if (isEdit) {
        await api.put(`/products/${id}`, form);
      } else {
        const { data } = await api.post("/products", form);
        productId = data.id;
      }
      navigate(`/print/${productId}`);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-8 py-7 min-h-screen">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 px-2 -ml-2 hover:bg-zinc-100"
            onClick={() => navigate("/")}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
              {isEdit ? "Edit Product" : "New Product"}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950" data-testid="editor-title">
              {form.product_name || "Untitled label"}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-10 gap-2 border-zinc-300"
            onClick={save}
            disabled={saving}
            data-testid="save-product-btn"
          >
            <Save size={15} />
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            className="h-10 bg-zinc-950 text-white hover:bg-zinc-800 gap-2"
            onClick={saveAndPrint}
            disabled={saving}
            data-testid="save-and-print-btn"
          >
            <Printer size={15} />
            Save &amp; Print
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Form (7/12) */}
        <div className="col-span-12 lg:col-span-7 space-y-5">
          {/* Reference sticker upload */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-zinc-900 flex items-center gap-1.5">
                  <Sparkles size={14} className="text-red-800" />
                  Auto-fill from existing sticker
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Upload a photo/scan — Claude Sonnet 4.5 reads it and auto-fills every field below.
                </div>
              </div>
              <div className="flex items-center gap-2">
                {refImage && !extracting && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 border-zinc-300"
                    onClick={() => refFile && autoExtract(refFile)}
                    data-testid="reextract-btn"
                  >
                    <Sparkles size={13} />
                    Re-extract
                  </Button>
                )}
                {refImage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-zinc-500 hover:text-red-700"
                    onClick={() => {
                      setRefImage(null);
                      setRefFile(null);
                    }}
                    data-testid="clear-reference-btn"
                    disabled={extracting}
                  >
                    <X size={14} />
                  </Button>
                )}
              </div>
            </div>
            {refImage ? (
              <div className="rounded-md border border-zinc-200 overflow-hidden bg-zinc-50 relative">
                <img
                  src={refImage}
                  alt="reference"
                  className="w-full max-h-72 object-contain"
                  data-testid="reference-image"
                />
                {extracting && (
                  <div
                    className="absolute inset-0 bg-white/80 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2"
                    data-testid="extracting-overlay"
                  >
                    <Loader2 className="animate-spin text-red-800" size={22} />
                    <div className="text-[12px] font-medium text-zinc-800">
                      Reading label with Claude Sonnet 4.5…
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      Usually takes 5-15 seconds
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => refImgRef.current?.click()}
                data-testid="upload-reference-btn"
                className="w-full flex flex-col items-center justify-center gap-2 py-8 border border-dashed border-zinc-300 rounded-md text-zinc-500 hover:bg-zinc-50 hover:border-zinc-400 transition-colors"
              >
                <Upload size={18} />
                <span className="text-[12px] font-medium">Click to upload sticker image</span>
                <span className="text-[10px] text-zinc-400">
                  PNG, JPG, WEBP · fields will fill in automatically
                </span>
              </button>
            )}
            <input
              ref={refImgRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickRefImage}
              data-testid="reference-file-input"
            />
          </section>

          {/* Label type selector */}
          <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="label-type-section">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-zinc-900">Label type</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Choose the sticker template. Attro Copper is 2.5&quot; × 2.5&quot;; Attro India / China are 3&quot; × 1.5&quot;.
                </div>
              </div>
              <Select
                value={form.label_type || "copper"}
                onValueChange={(v) => setForm((f) => ({ ...f, label_type: v }))}
              >
                <SelectTrigger className="w-56 h-9" data-testid="select-label-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="copper" data-testid="label-type-copper">Attro Copper (2.5 × 2.5 in)</SelectItem>
                  <SelectItem value="copper_customize" data-testid="label-type-copper-customize">Attro Copper Customize (2.5 × 2.5 in)</SelectItem>
                  <SelectItem value="india" data-testid="label-type-india">Attro India (3 × 1.5 in)</SelectItem>
                  <SelectItem value="china" data-testid="label-type-china">Attro China (3 × 1.5 in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Core fields */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="text-[13px] font-semibold text-zinc-900 mb-4">Label details</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Product Name *" testid="field-product-name">
                <Input value={form.product_name} onChange={set("product_name")} className="h-9" data-testid="input-product-name" />
              </Field>
              <Field label="Brand Name" testid="field-brand-name">
                <Input value={form.brand_name} onChange={set("brand_name")} className="h-9" data-testid="input-brand-name" />
              </Field>
              <Field label="Material Content">
                <Input value={form.material_content} onChange={set("material_content")} className="h-9" data-testid="input-material-content" />
              </Field>
              <Field label="Net Quantity">
                <Input value={form.net_quantity} onChange={set("net_quantity")} placeholder="e.g. 1U" className="h-9" data-testid="input-net-quantity" />
              </Field>
              <Field label="Actual Capacity">
                <Input value={form.actual_capacity} onChange={set("actual_capacity")} placeholder="e.g. 13000 ml each" className="h-9" data-testid="input-actual-capacity" />
              </Field>
              <Field label="Country of Origin">
                <Input value={form.country_of_origin} onChange={set("country_of_origin")} className="h-9" data-testid="input-country-of-origin" />
              </Field>
              <Field label="PKD (Month/Year)">
                <Input value={form.pkd_month_year} onChange={set("pkd_month_year")} placeholder="e.g. 01/2026" className="h-9" data-testid="input-pkd" />
              </Field>
              <Field label="Batch No">
                <Input value={form.batch_no} onChange={set("batch_no")} className="h-9" data-testid="input-batch-no" />
              </Field>
              <Field label={form.label_type === "copper_customize" ? "MRP (₹) — struck through" : "MRP (₹)"}>
                <Input value={form.mrp} onChange={set("mrp")} placeholder="e.g. 12999.00" className="h-9" data-testid="input-mrp" />
              </Field>
              {form.label_type === "copper_customize" && (
                <Field label="MOP (₹) — final price">
                  <Input value={form.mop} onChange={set("mop")} placeholder="e.g. 9999.00" className="h-9" data-testid="input-mop" />
                </Field>
              )}
              <Field label="Product Tagline">
                <Input value={form.tagline} onChange={set("tagline")} placeholder="e.g. SEA GREEN FLOWER GOLD DELUXE" className="h-9" data-testid="input-tagline" />
              </Field>
              <div className="col-span-2 flex items-center gap-2 pt-1">
                <Switch
                  checked={form.included_of_taxes}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, included_of_taxes: v }))}
                  data-testid="toggle-included-taxes"
                />
                <span className="text-[12px] text-zinc-700">Show &quot;(Included of all taxes)&quot; note under MRP</span>
              </div>
            </div>
          </section>

          {/* India / China-specific fields (shown only when label_type is India or China) */}
          {(form.label_type === "india" || form.label_type === "china") && (
            <section className="bg-white border border-zinc-200 rounded-md p-5" data-testid="india-china-fields">
              <div className="text-[13px] font-semibold text-zinc-900 mb-4">
                {form.label_type === "china" ? "Attro China" : "Attro India"} — extra fields
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Color">
                  <Input value={form.color} onChange={set("color")} placeholder="e.g. Silver" className="h-9" data-testid="input-color" />
                </Field>
                {form.label_type === "china" && (
                  <Field label="Model No">
                    <Input value={form.model_no} onChange={set("model_no")} placeholder="e.g. 7185" className="h-9" data-testid="input-model-no" />
                  </Field>
                )}
                <Field label="Packed On">
                  <Input value={form.packed_on} onChange={set("packed_on")} placeholder="e.g. May 2026" className="h-9" data-testid="input-packed-on" />
                </Field>
                <Field label="For any Query contact (email)">
                  <Input value={form.query_email} onChange={set("query_email")} placeholder="e.g. customercare@amworld.com" className="h-9" data-testid="input-query-email" />
                </Field>
                {form.label_type === "india" && (
                  <Field label="Customer Care (phone)">
                    <Input value={form.customer_care} onChange={set("customer_care")} placeholder="+91-7045352685" className="h-9" data-testid="input-customer-care" />
                  </Field>
                )}
                {form.label_type === "china" && (
                  <Field label="Customer Care (phone)">
                    <Input value={form.customer_care} onChange={set("customer_care")} placeholder="+91-7045352685" className="h-9" data-testid="input-customer-care-china" />
                  </Field>
                )}
              </div>
              <div className="mt-4">
                <Field label={form.label_type === "china" ? "Imported & Marketed By (multi-line)" : "Packed & Marketed By (multi-line)"}>
                  <Textarea
                    value={form.marketed_by}
                    onChange={set("marketed_by")}
                    placeholder="Company name and full address"
                    className="min-h-[80px] text-sm"
                    data-testid="input-marketed-by"
                  />
                </Field>
              </div>
              <div className="text-[11px] text-zinc-500 mt-3">
                Copper-only fields (ISI mark, Product image, Batch No, Mfd. &amp; Pkd. by, Connect with us, Brand Name) are hidden for this label type.
              </div>
            </section>
          )}

          {/* EAN + barcode (all label types) */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="text-[13px] font-semibold text-zinc-900 mb-4">Barcode</div>
            <Field label="Barcode value (numbers, letters, ASIN — anything)">
              <Input
                value={form.ean_code}
                onChange={set("ean_code")}
                placeholder="e.g. 8905435032270 or B07XJ8C8F5"
                className="h-9 font-mono"
                data-testid="input-ean-code"
              />
            </Field>
            <div className="text-[11px] text-zinc-500 mt-2">
              EAN13/EAN8 when the value is 13/8 digits, CODE128 for anything else (Amazon ASINs, SKUs, alphanumerics).
            </div>
          </section>

          {(form.label_type === "copper" || form.label_type === "copper_customize") && (
            <>
          {/* Address blocks */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="text-[13px] font-semibold text-zinc-900 mb-4">Address blocks</div>
            <div className="space-y-4">
              <Field label="Mfd. & Pkd. by">
                <Textarea
                  value={form.mfd_pkd_by}
                  onChange={set("mfd_pkd_by")}
                  placeholder="Company name and full address"
                  className="min-h-[70px] text-sm"
                  data-testid="input-mfd-pkd-by"
                />
              </Field>
              <Field label="Connect with us">
                <Textarea
                  value={form.connect_with_us}
                  onChange={set("connect_with_us")}
                  placeholder="Contact address, email, phone"
                  className="min-h-[70px] text-sm"
                  data-testid="input-connect-with-us"
                />
              </Field>
            </div>
          </section>

          {/* Product image */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="text-[13px] font-semibold text-zinc-900 mb-3">Product image on sticker</div>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden">
                {form.product_image ? (
                  <img src={form.product_image} alt="product" className="w-full h-full object-contain" data-testid="product-image-preview" />
                ) : (
                  <ImagePlus size={20} className="text-zinc-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 border-zinc-300 gap-2"
                  onClick={() => productImgRef.current?.click()}
                  data-testid="upload-product-image-btn"
                >
                  <Upload size={14} />
                  Upload
                </Button>
                {form.product_image && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-9 text-zinc-500 hover:text-red-700 gap-2"
                    onClick={() => setForm((f) => ({ ...f, product_image: "" }))}
                    data-testid="remove-product-image-btn"
                  >
                    <X size={14} />
                    Remove
                  </Button>
                )}
              </div>
              <input ref={productImgRef} type="file" accept="image/*" hidden onChange={onPickProductImage} />
            </div>
          </section>

          {/* ISI */}
          <section className="bg-white border border-zinc-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[13px] font-semibold text-zinc-900">ISI mark (optional)</div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.show_isi}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, show_isi: v }))}
                  data-testid="toggle-show-isi"
                />
                <span className="text-[12px] text-zinc-700">Show ISI on label</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="IS Standard Number">
                <Input value={form.isi_number} onChange={set("isi_number")} placeholder="IS 17803:2023" className="h-9" data-testid="input-isi-number" />
              </Field>
              <Field label="CM/L Number">
                <Input value={form.isi_cml} onChange={set("isi_cml")} placeholder="CM/L - 8700180113" className="h-9" data-testid="input-isi-cml" />
              </Field>
            </div>
          </section>
            </>
          )}
        </div>

        {/* Live preview (5/12) */}
        <div className="col-span-12 lg:col-span-5">
          <div className="sticky top-6 space-y-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Live preview</div>
            <div className="bg-zinc-100 rounded-lg p-6 flex items-center justify-center shadow-inner">
              <div className="shadow-xl" data-testid="label-preview">
              <StickerRenderer data={form} />
            </div>
            </div>
            <div className="text-[11px] text-zinc-500 text-center">
              {(form.label_type === "copper" || form.label_type === "copper_customize")
                ? "Sticker size: 2.5\" × 2.5\" · tiled A4 layout on the print screen"
                : "Sticker size: 3\" × 1.5\" · roll-form (one sticker per page) on the print screen"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, children }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <Label className="text-[11px] font-medium text-zinc-700 uppercase tracking-wider">
      {label}
    </Label>
    {children}
  </div>
);
